import { UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  DataTable,
  SortableHeadCell,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { FilterToggle } from "@/components/filter-toggle";
import { Highlight } from "@/components/highlight";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDateTime, formatNumber } from "@/lib/format";
import type { Period } from "@/lib/period";
import { matchesSearch } from "@/lib/search";
import { factLine } from "@/lib/table-facts";
import { sortRows, sortTime, useUrlTableSort } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

import { RowLink } from "../parts";
import { callsLink, formatFailures, personLabel, type PersonRow } from "../utils";

interface Props {
  rows: PersonRow[];
  days: Period;
}

/** Person, calls, failures, last call, the way to their calls. */
const COLUMNS = 5;

const SORT_COLUMNS = { person: "asc", calls: "desc", errors: "desc", last: "desc" } as const;

type SortKey = keyof typeof SORT_COLUMNS;

/**
 * Who used the connector in the period: each person by name with their
 * email, their calls, their failed calls with their share, and their last
 * call. Someone Velta blocked carries a read-only "Geblokkeerd" chip (the
 * Teams page blocks and unblocks). Every column sorts (kept in the URL); a
 * search, "Alleen met fouten" and "Geblokkeerd" filter it. A row leads to the
 * call log filtered on the person. On a phone the failures and the last call
 * move under the person.
 */
export function PersonTable({ rows, days }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const sort = useUrlTableSort<SortKey>({
    columns: SORT_COLUMNS,
    initial: { key: "calls", dir: "desc" },
    names: { sort: "personSort", dir: "personDir" },
  });

  if (rows.length === 0) {
    return (
      <p className="text-muted rounded-lg border py-8 text-center text-sm">
        {t("analytics.usersEmpty")}
      </p>
    );
  }

  const filtered = search !== "" || onlyErrors || onlyBlocked;
  const reset = () => {
    setSearch("");
    setOnlyErrors(false);
    setOnlyBlocked(false);
  };
  const matching = rows.filter((row) => matchesSearch([row.name, row.email, row.userOid], search));
  const shown = sortRows(
    matching.filter((row) => (!onlyErrors || row.errors > 0) && (!onlyBlocked || row.blocked)),
    sort.sort,
    (row, key) => {
      switch (key) {
        case "person":
          return personLabel(row);
        case "calls":
          return row.calls;
        case "errors":
          return row.errors;
        case "last":
          return sortTime(row.lastCallAt);
      }
    },
  );

  return (
    <div className="space-y-3">
      <FilterBar
        search={{ value: search, onChange: setSearch, label: t("analytics.usersSearch") }}
        columns={["auto", "auto"]}
        filtered={filtered}
        onReset={reset}
      >
        <FilterToggle
          label={t("analytics.onlyWithErrors")}
          count={matching.filter((row) => row.errors > 0).length}
          pressed={onlyErrors}
          onPressedChange={setOnlyErrors}
          className="w-full justify-between"
        />
        <FilterToggle
          label={t("analytics.blocked")}
          count={matching.filter((row) => row.blocked).length}
          pressed={onlyBlocked}
          onPressedChange={setOnlyBlocked}
          className="w-full justify-between"
        />
      </FilterBar>
      <DataTable label={t("analytics.byUser")}>
        <TableHead>
          <SortableHeadCell {...sort.head("person")}>{t("analytics.person")}</SortableHeadCell>
          <SortableHeadCell {...sort.head("calls")} align="right">
            {t("analytics.calls")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("errors")} align="right" narrow="hide">
            {t("analytics.errors")}
          </SortableHeadCell>
          {/* Leaves when the table's own room is tight (a narrow window beside the full sidebar). */}
          <SortableHeadCell {...sort.head("last")} narrow="hide" className="@max-3xl:hidden">
            {t("analytics.lastCall")}
          </SortableHeadCell>
          <TableHeadCell className="w-px">
            <span className="sr-only">{t("analytics.callsColumn")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {shown.map((row) => {
            const label = personLabel(row);
            return (
              <TableRow key={row.key}>
                <TableCell>
                  <TableItem
                    icon={
                      <UserRound
                        aria-hidden="true"
                        strokeWidth={1.75}
                        className={cn(TABLE_ICON_CLASS, row.blocked && "opacity-60")}
                      />
                    }
                    title={
                      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className={cn(row.name ? undefined : "break-all")}>
                          <Highlight text={label} query={search} />
                        </span>
                        {row.blocked && <BlockedChip />}
                      </span>
                    }
                    summary={row.name && row.email && <Highlight text={row.email} query={search} />}
                    facts={
                      <TableFacts>
                        {factLine([
                          t("analytics.failuresFact", {
                            failures: formatFailures(row.errors, row.calls),
                          }),
                          row.lastCallAt &&
                            t("analytics.lastCallFact", { time: formatDateTime(row.lastCallAt) }),
                        ])}
                      </TableFacts>
                    }
                    muted={row.blocked}
                  />
                </TableCell>
                <TableCell align="right" className="tabular-nums">
                  {formatNumber(row.calls)}
                </TableCell>
                <TableCell
                  align="right"
                  narrow="hide"
                  className={cn("whitespace-nowrap tabular-nums", row.errors === 0 && "text-muted")}
                >
                  {formatFailures(row.errors, row.calls)}
                </TableCell>
                <TableCell
                  narrow="hide"
                  className="text-muted whitespace-nowrap tabular-nums @max-3xl:hidden"
                >
                  {formatDateTime(row.lastCallAt)}
                </TableCell>
                <TableCell align="right" className="py-1 pr-1">
                  {row.userOid ? (
                    <RowLink
                      to={callsLink({ person: row.userOid }, days)}
                      label={t("analytics.callsOf", { name: label })}
                    />
                  ) : (
                    // Calls without sign-in have no id to filter on; keeps the column's edge.
                    <span aria-hidden="true" className="inline-block w-8 pointer-coarse:w-10" />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {shown.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={filtered ? reset : undefined}>
              {t("analytics.usersNoMatch")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
    </div>
  );
}

/** "Geblokkeerd", read-only, with what it means in its tooltip. */
function BlockedChip() {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Chip
          tone="danger"
          tabIndex={0}
          className="focus-visible:ring-ring self-center font-normal focus-visible:ring-2 focus-visible:outline-none"
        >
          {t("analytics.blocked")}
        </Chip>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 text-left">{t("analytics.blockedHint")}</TooltipContent>
    </Tooltip>
  );
}
